import { useMutation, useQueryClient } from '@tanstack/react-query';
import { regionsApi } from '../api/regions.api';
import { isRegionHierarchyQuery } from './isRegionHierarchyQuery';
import { regionKeys } from './queryKeys';

/**
 * Ids of every inactive descendant of `parentId`, ancestors before descendants — the API rejects
 * activating a child whose parent is still inactive. Active nodes are walked through but not
 * returned: they need no change, and an inactive grandchild may hang below one.
 */
// ponytail: 100 children per level, paginate if a region ever exceeds it.
const collectInactiveDescendantIds = async (parentId: string): Promise<string[]> => {
  const response = await regionsApi.getRegions({ parentId, pageSize: 100 });
  const children = response.data?.items ?? [];
  const nested = await Promise.all(children.map((child) => collectInactiveDescendantIds(child.id)));

  return children.flatMap((child, index) =>
    child.isActive ? nested[index] : [child.id, ...nested[index]],
  );
};

export const useChangeRegionStatus = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      await regionsApi.changeRegionStatus(id, isActive);
      if (!isActive) return;

      for (const descendantId of await collectInactiveDescendantIds(id)) {
        await regionsApi.changeRegionStatus(descendantId, isActive);
      }
    },
    onSettled: async () => {
      // regionKeys.all, not lists(): a status change (and its cascade) also moves the detail,
      // tree and byCountryAndType queries.
      await queryClient.invalidateQueries({ queryKey: regionKeys.all });
      await queryClient.invalidateQueries({ predicate: isRegionHierarchyQuery });
    },
  });

  return {
    updateStatus: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
};
